// Selected-process lens (DESIGN_SPEC §10.10): facts about the process picked in FIG Process tree.
// Props: FigProps { ctx, no } (no is unused: the lens is not a figure). ProcessTreeFig already renders this
// lens beside the tree; mount <ProcessLens/> yourself only to place it, and the tree then leaves it out.
//   meta(ctx) → { id: "process-lens", label: "Selected process", empty }   (not a figure-index section)
//   ProcessLensView { ctx, tree, node }   the aside.cell.s4.side.lens itself
//   selection store: selectProcess(jobId, key) · useSelectedKey(jobId) · useStandaloneLens(jobId) · resolveSelection(tree, key?)
import { useLayoutEffect, useSyncExternalStore, type MouseEvent, type ReactNode } from "react";
import { Kv, MT, Sev, type KvRow, type SevLevel } from "@/components/ui";
import { usePresignedDownload } from "@/hooks";
import { api } from "@/lib/api";
import { bytes, dur, rel } from "@/lib/format";
import type { DumpRef, FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import { reportVerdict } from "@/lib/derive/verdict";
import type { Job } from "@/types";
import { nodeAt, parentOf, treeOf, type Tree, type TreeNode } from "../derive/tree";

// ---- selection shared by the tree and a standalone lens (per job) ----------------------------

const selected = new Map<string, string>();
const lensMounts = new Map<string, number>();
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => {
    subs.delete(f);
  };
};

export function selectProcess(jobId: string, key: string) {
  if (selected.get(jobId) === key) return;
  selected.set(jobId, key);
  emit();
}

export function useSelectedKey(jobId: string): string | undefined {
  return useSyncExternalStore(subscribe, () => selected.get(jobId));
}

/** True while a standalone <ProcessLens/> for this job is mounted (the tree then omits its own). */
export function useStandaloneLens(jobId: string): boolean {
  return useSyncExternalStore(subscribe, () => (lensMounts.get(jobId) ?? 0) > 0);
}

/** The stored key when it still exists, else the first process a thread was written into, else the sample. */
export function resolveSelection(tree: Tree, key?: string): TreeNode | undefined {
  if (key && tree.nodes.has(key)) return tree.nodes.get(key);
  const traced = tree.order.map((k) => tree.nodes.get(k)!).filter((n) => !n.isContext && !n.external);
  return traced.find((n) => n.injectedBy.length > 0) ?? traced.find((n) => n.isSample) ?? traced[0];
}

export function meta(ctx: ReportCtx): FigMeta {
  return { id: "process-lens", label: "Selected process", empty: treeOf(ctx).inScope === 0 };
}

export default function ProcessLens({ ctx }: FigProps) {
  const jobId = ctx.job.id;
  useLayoutEffect(() => {
    lensMounts.set(jobId, (lensMounts.get(jobId) ?? 0) + 1);
    emit();
    return () => {
      lensMounts.set(jobId, Math.max(0, (lensMounts.get(jobId) ?? 1) - 1));
      emit();
    };
  }, [jobId]);
  const tree = treeOf(ctx);
  const node = resolveSelection(tree, useSelectedKey(jobId));
  return <ProcessLensView ctx={ctx} tree={tree} node={node} />;
}

// ---- the lens ----------------------------------------------------------------------------

/** Display name: the image's file name, else the job's sample for the agent's launch record. */
export function procName(n: TreeNode, job: Job): string {
  if (n.name) return n.name;
  if (n.isSample) return job.kind === "url" ? "browser" : job.filename || "sample";
  return n.isContext ? "parent process" : "unknown image";
}

/** exit 0 · exit 0xC0000005 (NTSTATUS in hex) */
export function exitText(code: number): string {
  const u = code >>> 0;
  return code < 0 || u >= 0x80000000 ? `0x${u.toString(16).toUpperCase().padStart(8, "0")}` : String(code);
}

function DumpLink({ jobId, dump }: { jobId: string; dump: DumpRef }) {
  const { download, pending } = usePresignedDownload();
  const name = dump.name ?? `pid ${dump.pid}`;
  const a = dump.artifact;
  if (!a)
    return (
      <span translate="no">
        {name}
        {dump.size !== undefined && <small> {bytes(dump.size)}</small>}
      </span>
    );
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    void download(jobId, a);
  };
  return (
    <span>
      <a className="lnk" href={api.artifactURL(jobId, a.sha256)} download={a.name} onClick={onClick} translate="no" aria-busy={pending === a.sha256 || undefined}>
        {name}
      </a>
      <small> {pending === a.sha256 ? "preparing…" : bytes(a.size || dump.size)}</small>
    </span>
  );
}

function list<T>(items: readonly T[], render: (x: T, i: number) => ReactNode): ReactNode {
  return items.map((x, i) => (
    <span key={i} className="block">
      {render(x, i)}
    </span>
  ));
}

export function ProcessLensView({ ctx, tree, node }: { ctx: ReportCtx; tree: Tree; node?: TreeNode }) {
  const job = ctx.job;
  if (!node)
    return (
      <aside className="cell s4 side lens" aria-label="Selected process">
        <p className="lens-eyebrow">Selected process</p>
        <p className="note">No processes</p>
      </aside>
    );
  const live = ctx.mode === "live";
  const collected = ctx.stream.transitions.some((t) => t.state === "collecting" || t.state === "completed");
  const parent = parentOf(tree, node);
  const nm = (pid: number, seq: number) => {
    const x = nodeAt(tree, pid, seq);
    return x ? procName(x, job) : "";
  };
  const rows: KvRow[] = [];
  if (node.image) rows.push(["Image", <MT value={node.image} tail="file" />]);
  if (!node.isContext && !node.external) {
    rows.push([
      "Parent",
      parent ? (
        <>
          <span translate="no">{procName(parent, job)}</span> <small>{parent.pid}{parent.isContext ? " · not traced" : ""}</small>
        </>
      ) : node.ppid ? (
        <small>pid {node.ppid} · not traced</small>
      ) : node.isSample ? (
        <small>launched by the guest agent</small>
      ) : null,
    ]);
    // argv[0] is the image itself: the arguments are the news; otherwise the command line verbatim.
    if (node.cmdline && node.args !== node.cmdline)
      rows.push([
        "Arguments",
        node.args ? (
          <span className="block text-left" translate="no" spellCheck={false}>
            {node.args}
          </span>
        ) : (
          <small>none</small>
        ),
      ]);
    else
      rows.push([
        "Command line",
        node.cmdline ? (
          <span className="block text-left" translate="no" spellCheck={false}>
            {node.cmdline}
          </span>
        ) : null,
      ]);
  }
  if (node.dropped)
    rows.push([
      "Dropped by",
      <>
        {node.dropped.byPid} <small>at {rel(node.dropped.t)}</small>
      </>,
    ]);
  if (node.injectedBy.length) {
    rows.push([
      "Thread written by",
      list(node.injectedBy, (i) => (
        <>
          {i.creatorPid} <small>at {rel(i.t)}</small>
        </>
      )),
    ]);
    rows.push([
      "Remote thread",
      list(node.injectedBy, (i) => (
        <>
          {i.tid !== undefined ? `tid ${i.tid}` : "tid —"}
          {i.startAddr && <small translate="no"> · start {i.startAddr}</small>}
        </>
      )),
    ]);
  }
  if (node.injectedInto.length)
    rows.push([
      "Threads written into",
      list(node.injectedInto, (i) => (
        <>
          <span translate="no">{nm(i.targetPid, i.seq) || "pid"}</span> <small>{i.targetPid} · {rel(i.t)}</small>
        </>
      )),
    ]);
  if (node.dumps.length) rows.push([node.dumps.length === 1 ? "Dump" : "Dumps", list(node.dumps, (d) => <DumpLink jobId={job.id} dump={d} />)]);
  for (const on of ["sample", "dump", "dropped"] as const) {
    const rules = [...new Set(node.yaraHits.filter((y) => y.on === on).map((y) => y.rule))];
    if (rules.length) rows.push([on === "sample" ? "YARA on sample" : on === "dump" ? "YARA on dump" : "YARA on the file", list(rules, (r) => <span translate="no">{r}</span>)]);
  }
  // The behavior tags whose evidence names this process's own events (documents only: no severities live).
  if (!live) {
    const byId = new Map((reportVerdict(ctx.stream, ctx.behaviorReport)?.tags ?? []).map((t) => [t.id, t]));
    const ids = new Set(node.seqs.flatMap((s) => ctx.cite.get(s)?.tagIds ?? []));
    const flagged = [...ids].flatMap((id) => byId.get(id) ?? []).sort((a, b) => b.severity - a.severity);
    if (flagged.length)
      rows.push([
        "Flagged by",
        <>
          {list(flagged.slice(0, 3), (t) => (
            <>
              <Sev level={Math.max(1, Math.min(5, t.severity)) as SevLevel} label={false} className="mr-2" />
              {t.title}
            </>
          ))}
          {flagged.length > 3 && <small>+ {flagged.length - 3} more</small>}
        </>,
      ]);
  }
  if (!node.isContext && !node.external) {
    const end = node.endT;
    rows.push([
      "Lifetime",
      end !== undefined ? (
        <>
          {dur(end - node.startT)}{" "}
          <small>
            · {rel(node.startT, 1)} › {rel(end, 1)}
          </small>
        </>
      ) : (
        <>
          {live ? "running" : collected ? "still running at collection" : "no exit recorded"} <small>· since {rel(node.startT, 1)}</small>
        </>
      ),
    ]);
    if (end !== undefined) rows.push(["Exit", node.exitCode !== undefined ? exitText(node.exitCode) : <small>code not recorded</small>]);
  }
  const acts = node.acts;
  const total = acts.load + acts.file + acts.reg + acts.net + acts.mutex;
  if (!node.isContext && !node.external && total === 0 && !live) rows.push(["Activity", <small>none recorded</small>]);
  return (
    <aside className="cell s4 side lens" aria-label={`Selected process: ${procName(node, job)} ${node.pid}`}>
      <p className="lens-eyebrow">
        Selected process · pid {node.pid}
        {node.isContext ? " · context" : node.external ? " · not traced" : ""}
      </p>
      <p className="lens-name" translate="no">
        {procName(node, job)}
      </p>
      <Kv rows={rows} />
      <p className="sr-only" aria-live="polite">
        {`${procName(node, job)} ${node.pid} selected.`}
      </p>
    </aside>
  );
}
